import React, { ChangeEvent, FormEvent, useEffect, useState } from 'react'
import {
  AtButton,
  AtButtonState,
  AtButtonVariant,
  AtCheckbox,
  AtRadio,
  getRichTextNode,
  MlFormField,
  MlHeading,
  TextAlignment,
} from '@curran-catalog/curran-atomic-library'
import { Select } from '@components/Select'
import { COUNTRIES } from '@utils/countries'
import { useAuth } from '@hooks/use-auth'
import { useRouter } from 'next/router'
import { fileInputClasses, professionOptions, yearsInBusinessOptions } from '@utils/trade'
import { STATES } from '@utils/states'
import { useTrade } from '@hooks/use-trade'
import LoadingView from '@components/LoadingView'
import { environment } from '@config/environment'
import { SiteDomain } from 'types'

export const TmTradeApply = () => {
  const [selectedBusinessCardFile, setSelectedBusinessCardFile] = useState<File>()
  const [selectedOtherFile, setSelectedOtherFile] = useState<File>()
  const [isDomestic, setIsDomestic] = useState(true)
  const [bypassDocuments, setBypassDocuments] = useState(false)
  const [error, setError] = useState<string>('')

  // Redirect to login if not logged in
  const { user, isLoading, isLoggedOut } = useAuth({})
  const [formIsLoading, setFormIsLoading] = useState<boolean>(false)
  const { apply } = useTrade()

  const router = useRouter()

  /* useEffect(() => {
    if (user?.trade_status === 'approved' || user?.trade_status === 'pending') {
      router.push('/registration-confirmation')
    }
  }, [user, router]) */
  useEffect(() => {
    if (user?.trade_status === 'approved') {
      router.push('/account') // Redirect to account page if approved
    } else if (user?.trade_status === 'pending') {
      router.push('/registration-confirmation') // Redirect to registration confirmation if pending
    }
  }, [user, router])

  const [formValues, setFormValues] = useState<{
    company: string
    website: string
    years_in_business: string
    address1: string
    address2: string
    city: string
    state: string | null
    zip: string
    country: string
    state_registration_number: string | null
    tax_exempt_id: string | null
    vat_id: string | null
    profession: string
    trade_newsletter: boolean
  }>({
    company: '',
    website: '',
    years_in_business: '',
    address1: '',
    address2: '',
    city: '',
    state: null,
    zip: '',
    country: isDomestic ? 'US' : '',
    state_registration_number: null,
    tax_exempt_id: null,
    vat_id: null,
    profession: '',
    trade_newsletter: true,
  })

  const changeBusinessCardFileHandler = (event: ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) {
      return
    }

    setSelectedBusinessCardFile(event.target.files[0])
  }

  const changeOtherFileHandler = (event: ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) {
      return
    }

    setSelectedOtherFile(event.target.files[0])
  }

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setFormIsLoading(true)

    apply({
      ...formValues,
      bypass_documents: bypassDocuments,
      business_card_document: selectedBusinessCardFile,
      document: selectedOtherFile,
      setError,
    })
      .then(() => {
        router.push('/registration-confirmation')
      })
      .finally(() => setFormIsLoading(true))
  }

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    setFormValues({ ...formValues, [e.target.name]: e.target.value })
  }

  const changeDomesticRadioButtonHandler = (isDomesticSelected: boolean) => {
    setIsDomestic(isDomesticSelected)
    setFormValues({ ...formValues, country: isDomesticSelected ? 'US' : '' })
  }

  if (isLoggedOut) {
    return (
      <div className="sm:text-center sm:py-12">
        <p>Before you apply for our Trade Program, please sign in or create an account.</p>
        <div className="mt-6 flex flex-col sm:flex-row gap-4 justify-center">
          <AtButton label="Login" onClick={() => router.push('/login?intended=trade%2Fapply')} />

          <AtButton
            label="Create Account"
            onClick={() => router.push('/register?intended=trade%2Fapply')}
            variant={AtButtonVariant.SECONDARY}
          />
        </div>
      </div>
    )
  } else if (isLoading || user?.trade_status === 'pending' || user?.trade_status === 'approved') {
    return <LoadingView message="Loading..." />
  }

  return (
    <div className="mx-auto lg:max-w-2xl">
      <MlHeading title={getRichTextNode('Trade Program Application', 'heading-1')} textAlignment={TextAlignment.LEFT} />

      <form onSubmit={handleSubmit}>
        <div className="mt-12">
          <p>
            In addition to filling out the form below, we require that you submit a business card or company letterhead,
            as well as documentation of your business license, tax exempt status, or VAT registration (non-U.S.
            applicants only).
          </p>
          <p className="mt-4 text-gray-600">
            <b>Important note:</b> To qualify for U.S. sales tax exemption, you must upload an up-to-date Resale or
            Sales Tax Certificate as your second document.
          </p>
          <div className="mt-8">
            <label className="font-medium">1. Business Card or Letterhead</label>
            <div className="custom-file">
              <input
                type="file"
                onChange={changeBusinessCardFileHandler}
                className={fileInputClasses}
                id="customFile"
                name="business_card_document"
                accept=".jpeg,.jpg,.pdf"
                required={bypassDocuments ? false : true}
              />
              <span className="text-xs mt-1">Max 10MB; JPEG or PDF</span>
            </div>
          </div>
          <div className="mt-4">
            <label className="font-medium">2. One of the below documents:</label>
            <ul className="list-disc pl-4 ml-4">
              <li>Current Business License</li>
              <li>Resale or Sales Tax Certificate</li>
              <li>VAT Registration (non-U.S. applicants only)</li>
            </ul>
            <div className="mt-1">
              <input
                type="file"
                onChange={changeOtherFileHandler}
                className={fileInputClasses}
                id="customFile2"
                name="document"
                accept=".jpeg,.jpg,.pdf"
                required={bypassDocuments ? false : true}
              />
              <span className="text-xs mt-1">Max 10MB; JPEG or PDF</span>
            </div>
          </div>

          <hr className="mt-4 border-outlined-gray" />

          <div className="mt-4" id="help_section">
            <AtCheckbox
              type="checkbox"
              name="bypass_documents"
              label="I am having trouble uploading my documents and request customer service to follow up over email or phone after I submit my application."
              id="bypass_documents"
              onChange={() => setBypassDocuments(!bypassDocuments)}
            />
          </div>
        </div>

        <div className="mt-12 flex flex-col gap-y-6">
          <div>
            <label>Country</label>
            <div className="flex gap-x-4">
              <AtRadio
                type="radio"
                name="country"
                value="US"
                label="US"
                checked={isDomestic}
                onChange={(e) => changeDomesticRadioButtonHandler(e.target.value === 'US')}
              />
              <AtRadio
                type="radio"
                name="country"
                value="other"
                label="Other"
                checked={!isDomestic}
                onChange={(e) => changeDomesticRadioButtonHandler(e.target.value === 'US')}
              />
            </div>
          </div>
          <MlFormField
            value={formValues.company}
            onChange={onChange}
            type="text"
            id="company"
            name="company"
            label="Company Name"
            required
            autoComplete="organization"
          />
          <MlFormField
            value={formValues.website}
            onChange={onChange}
            type="text"
            id="companyWebsite"
            name="website"
            label="Company Website (optional)"
            autoComplete="url"
          />
          <Select
            name="years_in_business"
            label="Years in Business"
            value={formValues.years_in_business}
            onChange={(value: string) => setFormValues({ ...formValues, years_in_business: value })}
            options={yearsInBusinessOptions}
            required
          />
          <MlFormField
            value={formValues.address1}
            onChange={onChange}
            type="text"
            id="address1"
            name="address1"
            label="Business Address"
            required
            autoComplete="address-line1"
          />
          <MlFormField
            value={formValues.address2}
            onChange={onChange}
            type="text"
            id="address2"
            name="address2"
            label="Apt, Suite, Building (optional)"
            autoComplete="address-line2"
          />
          <MlFormField
            value={formValues.city}
            onChange={onChange}
            type="text"
            id="city"
            name="city"
            label="City"
            required
            autoComplete="address-level2"
          />
          <div className="flex gap-x-4">
            <div className="flex-1">
              {isDomestic ? (
                <Select
                  name="state"
                  label="State"
                  value={formValues.state}
                  onChange={(value: string) => setFormValues({ ...formValues, state: value })}
                  options={STATES}
                  required
                />
              ) : (
                <MlFormField
                  value={formValues.state ?? ''}
                  onChange={onChange}
                  type="text"
                  id="state"
                  name="state"
                  label="State/Province"
                  required
                />
              )}
            </div>
            <div className="flex-1">
              <MlFormField
                value={formValues.zip}
                onChange={onChange}
                type="text"
                id="zip"
                name="zip"
                label={isDomestic ? 'Zip Code' : 'Postal Code'}
                required
                autoComplete="postal-code"
              />
            </div>
          </div>
          {!isDomestic ? (
            <Select
              label="Country (where business registered)"
              name="country"
              options={COUNTRIES.filter((c) => c.value !== 'US')}
              value={formValues.country}
              onChange={(value: string) => setFormValues({ ...formValues, country: value })}
              required
            />
          ) : (
            <>
              <MlFormField
                type="text"
                value={formValues.state_registration_number ?? ''}
                onChange={onChange}
                id="stateRegistrationNumber"
                name="state_registration_number"
                label="State Business Registration Number"
                required
              />
              <MlFormField
                value={formValues.tax_exempt_id ?? ''}
                onChange={onChange}
                type="text"
                id="taxExemptId"
                name="tax_exempt_id"
                label="Reseller's Tax Exempt ID (optional)"
              />
            </>
          )}
          {!isDomestic && (
            <MlFormField
              value={formValues.vat_id ?? ''}
              onChange={onChange}
              type="text"
              id="vatId"
              name="vat_id"
              label="VAT Number"
            />
          )}

          <Select
            label="Which best describes your profession?"
            name="profession"
            value={formValues.profession}
            onChange={(value: string) => setFormValues({ ...formValues, profession: value })}
            options={professionOptions}
            required
          />
          <AtCheckbox
            type="checkbox"
            name="tradeNewsletter"
            label="Join our Trade Customer mailing list to receive exclusive sale alerts and updates."
            id="tradeNewsletter"
            checked={formValues.trade_newsletter}
            onChange={(e) => setFormValues({ ...formValues, trade_newsletter: e.target.checked })}
          />
          {error && <div className="mt-4 text-error text-sm">{error}</div>}
          <AtButton
            type="submit"
            label={formIsLoading ? 'Loading...' : 'Apply'}
            className="block w-full"
            state={formIsLoading ? AtButtonState.LOADING : AtButtonState.ENABLED}
          />
          <div className="mt-1 text-sm">
            <p>Applications will be approved within 1 business day for all qualified trade professionals.</p>
            <p>
              If you have any questions, please call us at{' '}
              <a href={environment.siteDomain === SiteDomain.SISALCARPET ? 'tel:877-75-74725' : 'tel:800-555-6653'}>
                {environment.siteDomain === SiteDomain.SISALCARPET ? '877-75-74725' : '800-555-6653'}
              </a>{' '}
              or email{' '}
              <a
                href={
                  environment.siteDomain === SiteDomain.SISALCARPET
                    ? 'mailto:service@sisalcarpet.com'
                    : 'mailto:service@curranonline.com'
                }
              >
                {environment.siteDomain === SiteDomain.SISALCARPET
                  ? 'service@sisalcarpet.com'
                  : 'service@curranonline.com'}
              </a>
              .
            </p>
          </div>
        </div>
      </form>
    </div>
  )
}
